<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载管理数据">
    <div class="admin-page">
      <div class="admin-toolbar">
        <nav class="module-tabs" role="tablist" aria-label="管理模块">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            class="module-tab"
            :class="{ on: tab === t.key }"
            role="tab"
            :id="`admin-tab-${t.key}`"
            aria-controls="admin-workspace"
            :aria-selected="tab === t.key ? 'true' : 'false'"
            @click="tab = t.key"
          >
            <i :class="t.icon" aria-hidden="true"></i>
            <span>{{ t.label }}</span>
          </button>
        </nav>

        <div class="gateway-state" :class="{ ok: gateway.healthy }" aria-live="polite">
          <span class="gateway-dot" aria-hidden="true"></span>
          <span class="gateway-copy">
            <small>微信网关</small>
            <strong>{{ gateway.healthy ? "运行正常" : "状态异常" }}</strong>
          </span>
          <span class="gateway-uptime">{{ gateway.uptime }}</span>
        </div>
      </div>

      <section id="admin-workspace" class="admin-workspace" role="tabpanel" :aria-labelledby="`admin-tab-${tab}`">
        <div :key="tab" class="workspace-content">

          <!-- 用户额度 -->
          <div v-if="tab === 'users'" class="module-panel users-panel">
            <div class="section-note">
              <span>共 {{ users.length }} 位用户</span>
              <small>本月额度达到 95% 时自动显示告警</small>
            </div>
            <el-table :data="users" class="admin-table users-table" empty-text="暂无用户数据">
              <el-table-column label="用户" min-width="220">
                <template #default="{ row }">
                  <div class="user-cell">
                    <span class="user-avatar" aria-hidden="true">{{ (row.nickname || row.email || "用").trim().slice(0, 1).toUpperCase() }}</span>
                    <span class="user-copy">
                      <span class="user-name-line">
                        <b>{{ row.nickname || "未设置昵称" }}</b>
                        <em class="role-tag" :class="row.role">{{ row.role === "admin" ? "管理员" : "用户" }}</em>
                      </span>
                      <small>{{ row.email }}</small>
                    </span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="当前套餐" min-width="206">
                <template #default="{ row }">
                  <div
                    class="sub-cell"
                    :class="{
                      empty: !row.planName,
                      expired: row.subscription && row.subscription.status === 'expired',
                    }"
                  >
                    <span class="sub-accent" aria-hidden="true"></span>
                    <span class="sub-copy">
                      <span class="sub-name-line">
                        <b>{{ row.planName || "未开通" }}</b>
                        <em v-if="row.planName">{{ row.subscription && row.subscription.status === "expired" ? "已到期" : "使用中" }}</em>
                      </span>
                      <small v-if="row.subscription && row.subscription.expires_at">有效期至 {{ row.subscription.expires_at.slice(0, 10) }}</small>
                      <small v-else>尚未分配有效套餐</small>
                    </span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="消息额度 · 本月" min-width="190">
                <template #default="{ row }">
                  <div class="quota-cell">
                    <div class="quota-copy">
                      <span><b>{{ Number(row.used || 0).toLocaleString("zh-CN") }}</b> / {{ row.quota > 0 ? Number(row.quota).toLocaleString("zh-CN") : "不限" }} 条</span>
                      <em v-if="row.quota > 0">{{ Math.min(100, Math.round((row.used / row.quota) * 100)) }}%</em>
                    </div>
                    <el-progress
                      :percentage="row.quota > 0 ? Math.min(100, Math.round((row.used / row.quota) * 100)) : 0"
                      :show-text="false"
                      :stroke-width="6"
                    />
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="账户状态" width="104">
                <template #default="{ row }">
                  <span class="status-tag" :class="{ warn: row.status !== '正常', banned: row.status === '已封禁' }">
                    <i aria-hidden="true"></i>{{ row.status }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="278" align="right">
                <template #default="{ row }">
                  <div class="row-actions">
                    <button type="button" title="调整本月消息额度" @click="openQuota(row)">调额度</button>
                    <button type="button" title="调整用户套餐" @click="openUserSubscription(row)">改套餐</button>
                    <button type="button" title="按当前套餐规则刷新额度" @click="refreshUserSubscription(row)">刷新套餐</button>
                    <button type="button" title="查看用户角色卡" @click="openUserCards(row)">角色卡</button>
                    <button
                      v-if="row.role !== 'admin'"
                      type="button"
                      :class="row.isActive ? 'danger' : 'success'"
                      @click="toggleUserActive(row)"
                    >{{ row.isActive ? "封禁" : "解封" }}</button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div v-else-if="tab === 'system-cards'" class="module-panel system-cards-panel">
            <div class="section-note">
              <span>共 {{ systemRoleCards.length }} 张系统角色卡</span>
              <small>“指定用户内测”仅影响角色卡发现、新建和切换；存量伴侣继续可用</small>
            </div>
            <el-table :data="systemRoleCards" class="admin-table" empty-text="暂无系统角色卡">
              <el-table-column prop="id" label="ID" width="72" />
              <el-table-column label="角色卡" min-width="220">
                <template #default="{ row }">
                  <div class="entity-cell">
                    <span class="entity-icon"><i class="el-icon-postcard"></i></span>
                    <span><b>{{ row.name }}</b><small>{{ row.description || "暂无描述" }}</small></span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="投放范围" width="190">
                <template #default="{ row }">
                  <el-select
                    v-model="row.access_mode"
                    size="mini"
                    :disabled="row.accessModeSaving"
                    @change="saveSystemCardAccessMode(row)"
                  >
                    <el-option label="公开" value="public" />
                    <el-option label="指定用户内测" value="allowlist" />
                    <el-option label="仅管理员可见" value="hidden" />
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column label="更新时间" width="120">
                <template #default="{ row }">{{ (row.updated_at || "").slice(0, 10) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="130" align="right">
                <template #default="{ row }">
                  <div class="row-actions single">
                    <button type="button" @click="openRoleCardAccess(row)">管理内测用户</button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div v-else-if="tab === 'plans'" class="module-panel plans-panel">
            <div class="section-note">
              <span>共 {{ plans.length }} 个套餐</span>
              <small>保存后，新购买与手动刷新将按最新规则生效</small>
            </div>
            <el-table :data="plans" class="admin-table plans-table" empty-text="暂无套餐数据">
              <el-table-column label="套餐" min-width="190">
                <template #default="{ row }">
                  <div class="plan-name-cell">
                    <span class="plan-swatch" :style="{ backgroundColor: row.accent || 'var(--xyo-primary)' }" aria-hidden="true"></span>
                    <span><b>{{ row.name }}</b><small>{{ row.code }}</small></span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="monthly_price" label="月价" min-width="110">
                <template #default="{ row }"><b class="price-value">¥{{ Number(row.monthly_price || 0).toFixed(2) }}</b></template>
              </el-table-column>
              <el-table-column label="消息" min-width="90">
                <template #default="{ row }">{{ row.message_limit }}</template>
              </el-table-column>
              <el-table-column label="生图" min-width="82">
                <template #default="{ row }">{{ row.image_limit }}</template>
              </el-table-column>
              <el-table-column label="语音" min-width="82">
                <template #default="{ row }">{{ row.voice_limit }}</template>
              </el-table-column>
              <el-table-column prop="sort_order" label="排序" width="74" />
              <el-table-column label="推荐" width="92">
                <template #default="{ row }"><span class="recommend-tag" :class="{ on: row.is_recommended }">{{ row.is_recommended ? "推荐" : "普通" }}</span></template>
              </el-table-column>
              <el-table-column label="操作" width="84" align="right">
                <template #default="{ row }">
                  <div class="row-actions single"><button type="button" @click="openPlanEdit(row)">编辑</button></div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 全部伴侣 -->
          <div v-else-if="tab === 'companions'" class="module-panel companions-panel">
            <div class="section-note">
              <span>共 {{ companions.length }} 个伴侣</span>
              <small>查看归属、微信绑定与创建时间</small>
            </div>
            <el-table :data="companions" class="admin-table companions-table" empty-text="暂无伴侣数据">
              <el-table-column label="伴侣" min-width="180">
                <template #default="{ row }">
                  <div class="entity-cell"><span class="entity-icon"><i class="el-icon-user"></i></span><span><b>{{ row.name }}</b><small>ID {{ row.id }}</small></span></div>
                </template>
              </el-table-column>
              <el-table-column prop="owner" label="所属用户" min-width="180" />
              <el-table-column prop="wechat" label="微信号" min-width="180" />
              <el-table-column prop="status" label="绑定状态" width="112">
                <template #default="{ row }">
                  <span class="status-tag" :class="{ bound: row.status === '已绑定' }"><i aria-hidden="true"></i>{{ row.status }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="createdAt" label="创建时间" width="130" />
              <el-table-column label="操作" width="170" align="right">
                <template #default="{ row }">
                  <div class="row-actions">
                    <button type="button" @click="openCompEdit(row)">编辑</button>
                    <button type="button" @click="adminUnbind(row)">解绑</button>
                    <button type="button" class="danger" @click="adminDelete(row)">删除</button>
                  </div>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 审计日志 -->
          <div v-else-if="tab === 'audit'" class="module-panel audit-panel">
            <div class="section-note">
              <span>最近 {{ logs.length }} 条记录</span>
              <small>系统关键管理操作留痕</small>
            </div>
            <el-table :data="logs" class="admin-table audit-table" empty-text="暂无审计记录">
              <el-table-column prop="time" label="时间" width="180" />
              <el-table-column prop="actor" label="操作者" min-width="140" />
              <el-table-column prop="action" label="动作" min-width="180">
                <template #default="{ row }"><code class="action-code">{{ row.action }}</code></template>
              </el-table-column>
              <el-table-column prop="target" label="对象" min-width="220" />
              <el-table-column prop="ip" label="IP" width="140" />
            </el-table>
          </div>

          <!-- 系统提示词 -->
          <div v-else-if="tab === 'prompt'" class="module-panel prompt-panel">
            <div class="admin-callout">
              <span class="callout-icon"><i class="el-icon-document"></i></span>
              <span class="callout-copy">
                <b>需要管理完整提示词库？</b>
                <small>说话规则、工具、检测词和婉拒话术等 26 项可在独立页面导入、导出与恢复。</small>
              </span>
              <button type="button" @click="$router.push('/admin/prompts')">进入提示词管理 <i class="el-icon-right"></i></button>
            </div>
            <el-form label-position="top" class="prompt-form">
              <el-form-item label="系统级提示词">
                <el-input
                  v-model="globalPrompt.prompt"
                  type="textarea"
                  :rows="14"
                  placeholder="这里的提示词会叠加到所有角色卡和伴侣的人设之前，对整个系统生效。"
                />
              </el-form-item>
              <div class="prompt-meta">
                <span>对所有角色即时生效，适合配置统一口吻、边界、工具规则和全局人格约束。</span>
                <span v-if="globalPrompt.updatedAt">上次更新 {{ globalPrompt.updatedAt.replace("T", " ").slice(0, 19) }}</span>
              </div>
              <el-button type="primary" round :loading="promptSaving" @click="saveGlobalPrompt">保存系统提示词</el-button>
            </el-form>
          </div>

          <!-- SMTP -->
          <div v-else-if="tab === 'smtp'" class="module-panel smtp-panel">
            <div class="config-status" :class="{ ok: smtp.configured }"><i aria-hidden="true"></i>{{ smtp.configured ? "邮件服务已配置" : "邮件服务尚未配置" }}</div>
            <el-form label-position="top" class="smtp-form">
              <div class="form-grid smtp-grid">
                <el-form-item label="SMTP 主机"><el-input v-model="smtp.host" /></el-form-item>
                <el-form-item label="端口"><el-input v-model="smtp.port" /></el-form-item>
                <el-form-item label="账号"><el-input v-model="smtp.username" /></el-form-item>
                <el-form-item label="发件人"><el-input v-model="smtp.sender" /></el-form-item>
              </div>
              <div class="smtp-security-row">
                <el-form-item label="密码">
                  <el-input v-model="smtp.password" type="password" placeholder="已配置则留空不修改" show-password />
                </el-form-item>
                <el-form-item class="ssl-field" label="传输安全">
                  <el-checkbox v-model="smtp.ssl">启用 SSL/TLS</el-checkbox>
                </el-form-item>
              </div>
              <div class="form-actions">
                <el-button type="primary" round @click="saveSmtp">保存配置</el-button>
                <el-button round @click="testVisible = true">发送测试邮件</el-button>
              </div>
            </el-form>
          </div>

          <!-- 网关状态 -->
          <div v-else class="module-panel gateway-panel">
            <div class="gateway-settings">
              <div class="setting-row">
                <div>
                  <b>消息聚合窗口</b>
                  <p>同一微信对端在窗口内连续发来的消息会合并成一组，再统一发给模型。</p>
                </div>
                <div class="setting-actions">
                  <el-input-number v-model="gatewayConfig.message_buffer_window_ms" :min="0" :max="60000" :step="1000" />
                  <span class="unit">ms</span>
                  <el-button type="primary" round size="small" :loading="gatewayConfigSaving" @click="saveGatewayConfig">保存</el-button>
                </div>
              </div>
              <div class="setting-row">
                <div>
                  <b>大模型处理中可打断</b>
                  <p>收到同一聊天对象的新消息时，中断当前轮并带上新消息重新生成。</p>
                </div>
                <div class="setting-actions"><el-switch v-model="gatewayConfig.interrupt_generation" /></div>
              </div>
            </div>
            <div class="gw-summary">
              <div><span>网关健康</span><b :class="{ ok: gateway.healthy }">{{ gateway.healthy ? "正常" : "异常" }}</b></div>
              <div><span>运行状态</span><b>{{ gateway.uptime }}</b></div>
              <div><span>在线微信号</span><b>{{ onlineCount }} <small>/ {{ gateway.accounts.length }}</small></b></div>
            </div>
            <div class="gateway-accounts-head"><b>微信账号</b><small>最近一次连接状态</small></div>
            <el-table :data="gateway.accounts" class="admin-table gateway-table" empty-text="暂无微信账号">
              <el-table-column prop="id" label="账号 ID" min-width="180" />
              <el-table-column prop="nickname" label="昵称" min-width="180" />
              <el-table-column label="在线状态" width="120">
                <template #default="{ row }"><span class="status-tag" :class="row.online ? 'bound' : 'off'"><i aria-hidden="true"></i>{{ row.online ? "在线" : "离线" }}</span></template>
              </el-table-column>
              <el-table-column prop="lastSeen" label="最近活跃" min-width="180" />
            </el-table>
          </div>
        </div>
      </section>
    </div>

    <!-- 调整额度 -->
    <el-dialog title="调整用户额度" :visible.sync="quotaVisible" width="380px" custom-class="yw-dialog">
      <el-form label-position="top" v-if="quotaRow">
        <el-form-item label="用户"><el-input :value="quotaRow.nickname" disabled /></el-form-item>
        <el-form-item label="本月消息额度（条，0=不限）"><el-input-number v-model="quotaRow.quota" :step="50" :min="0" style="width: 100%" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button round @click="quotaVisible = false">取消</el-button>
        <el-button type="primary" round @click="saveQuota">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="调整用户套餐" :visible.sync="userSubVisible" width="420px" custom-class="yw-dialog">
      <el-form label-position="top" v-if="userSubRow">
        <el-form-item label="用户"><el-input :value="userSubRow.nickname" disabled /></el-form-item>
        <el-form-item label="当前套餐"><el-input :value="userSubRow.planName || '未开通'" disabled /></el-form-item>
        <el-form-item label="切换套餐">
          <el-select v-model="userSubPlanCode" style="width: 100%">
            <el-option v-for="plan in plans" :key="plan.code" :label="`${plan.name}（${plan.code}）`" :value="plan.code" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button round @click="userSubVisible = false">取消</el-button>
        <el-button type="primary" round :loading="userSubSaving" @click="saveUserSubscription">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="编辑套餐" :visible.sync="planVisible" width="520px" custom-class="yw-dialog">
      <el-form label-position="top" v-if="planForm">
        <div class="form-grid">
          <el-form-item label="编码"><el-input v-model="planForm.code" disabled /></el-form-item>
          <el-form-item label="名称"><el-input v-model="planForm.name" /></el-form-item>
          <el-form-item label="月价"><el-input-number v-model="planForm.monthly_price" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="排序"><el-input-number v-model="planForm.sort_order" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="消息额度"><el-input-number v-model="planForm.message_limit" :min="0" :step="10" style="width: 100%" /></el-form-item>
          <el-form-item label="生图额度"><el-input-number v-model="planForm.image_limit" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="语音额度"><el-input-number v-model="planForm.voice_limit" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="伴侣数上限(0=不限)"><el-input-number v-model="planForm.companion_limit" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="角色卡上限(0=不限)"><el-input-number v-model="planForm.role_card_limit" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="微信接管天数(0=不限)"><el-input-number v-model="planForm.wechat_takeover_days" :min="0" :step="1" style="width: 100%" /></el-form-item>
          <el-form-item label="主题色"><el-input v-model="planForm.accent" /></el-form-item>
          <el-form-item label="图标"><el-input v-model="planForm.icon" /></el-form-item>
        </div>
        <el-form-item label="描述"><el-input v-model="planForm.description" type="textarea" :rows="3" /></el-form-item>
        <el-form-item><el-checkbox v-model="planForm.is_recommended">设为推荐套餐</el-checkbox></el-form-item>
        <el-form-item><el-checkbox v-model="planForm.allow_role_sharing">允许私享分享</el-checkbox></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button round @click="planVisible = false">取消</el-button>
        <el-button type="primary" round :loading="planSaving" @click="savePlan">保存</el-button>
      </span>
    </el-dialog>

    <!-- 用户角色卡 -->
    <el-dialog :title="cardsUser ? `「${cardsUser.nickname}」的角色卡` : '用户角色卡'" :visible.sync="cardsVisible" width="640px" custom-class="yw-dialog">
      <el-table :data="userCards" stripe v-loading="cardsLoading">
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="name" label="名称" width="160" />
        <el-table-column prop="description" label="描述" show-overflow-tooltip />
        <el-table-column label="更新时间" width="120">
          <template #default="{ row }">{{ (row.updated_at || "").slice(0, 10) }}</template>
        </el-table-column>
      </el-table>
      <p v-if="!cardsLoading && !userCards.length" class="empty-tip">该用户没有自建角色卡</p>
    </el-dialog>

    <!-- 系统角色卡内测授权 -->
    <el-dialog
      :visible.sync="accessUsersVisible"
      width="720px"
      custom-class="yw-dialog access-users-dialog"
    >
      <div slot="title" class="access-dialog-title">
        <span class="access-title-icon" aria-hidden="true"><i class="el-icon-user-solid"></i></span>
        <span class="access-title-copy">
          <b>{{ accessCard ? accessCard.name : "系统角色卡" }}</b>
          <small>内测用户管理</small>
        </span>
      </div>

      <div class="access-dialog-body">
        <div class="access-intro">
          <i class="el-icon-info" aria-hidden="true"></i>
          <p>开通后用户可以发现并用该卡创建伴侣；撤销资格不会影响已经创建的伴侣。</p>
        </div>

        <div class="access-toolbar">
          <el-input
            v-model.trim="accessUserQuery"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索昵称或邮箱"
            aria-label="搜索内测用户"
          />
          <span class="access-summary">
            <b>{{ grantedOrdinaryUserCount }}</b>
            <small>位用户已开通</small>
          </span>
        </div>

        <div class="access-list" v-loading="accessUsersLoading">
          <div class="access-list-head" aria-hidden="true">
            <span>用户</span>
            <span>账号状态</span>
            <span>内测资格</span>
          </div>
          <div
            v-for="row in filteredAccessUsers"
            :key="row.id"
            class="access-user-row"
            :class="{ admin: row.role === 'admin', banned: !row.isActive }"
          >
            <div class="access-user-identity">
              <span class="access-user-avatar" :style="accessAvatarStyle(row)" aria-hidden="true">
                {{ accessAvatarLabel(row) }}
              </span>
              <span class="access-user-copy">
                <span class="access-user-name-line">
                  <b>{{ row.nickname || "未设置昵称" }}</b>
                  <em v-if="row.role === 'admin'" class="access-role-badge">管理员</em>
                </span>
                <small :title="row.email">{{ row.email }}</small>
              </span>
            </div>

            <div class="access-account-state">
              <span class="access-state-pill" :class="{ off: !row.isActive }">
                <i aria-hidden="true"></i>{{ row.isActive ? "正常" : "已封禁" }}
              </span>
            </div>

            <div class="access-control">
              <el-switch
                :value="row.role === 'admin' || hasRoleCardAccess(row.id)"
                :disabled="row.role === 'admin' || accessSavingUserId === row.id"
                active-color="#6f56d9"
                :aria-label="`${row.nickname || row.email}的内测资格`"
                @change="toggleRoleCardAccess(row)"
              />
              <span
                class="access-control-label"
                :class="{ on: row.role === 'admin' || hasRoleCardAccess(row.id) }"
              >
                {{ row.role === "admin" ? "默认可用" : hasRoleCardAccess(row.id) ? "已开通" : "未开通" }}
              </span>
            </div>
          </div>

          <div v-if="!accessUsersLoading && !filteredAccessUsers.length" class="access-empty">
            <i class="el-icon-search" aria-hidden="true"></i>
            <b>没有找到匹配用户</b>
            <small>换一个昵称或邮箱试试</small>
          </div>
        </div>
      </div>

      <span slot="footer" class="access-dialog-footer">
        <small>修改后立即生效</small>
        <el-button type="primary" round @click="accessUsersVisible = false">完成</el-button>
      </span>
    </el-dialog>

    <!-- 管理员编辑伴侣 -->
    <el-dialog title="编辑伴侣" :visible.sync="compEditVisible" width="480px" custom-class="yw-dialog">
      <el-form label-position="top" v-if="compForm">
        <el-form-item label="名称"><el-input v-model="compForm.name" /></el-form-item>
        <el-form-item label="描述"><el-input v-model="compForm.description" /></el-form-item>
        <el-form-item label="角色卡 ID"><el-input-number v-model="compForm.role_card_id" :min="1" style="width: 100%" /></el-form-item>
        <el-form-item label="提示词补充"><el-input type="textarea" :rows="3" v-model="compForm.custom_prompt" /></el-form-item>
        <el-form-item label="历史消息条数"><el-input-number v-model="compForm.message_history_limit" :min="4" :max="50" style="width: 100%" /></el-form-item>
        <el-form-item label="基础亲密度"><el-slider v-model="compForm.intimacy_base_level" :max="100" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button round @click="compEditVisible = false">取消</el-button>
        <el-button type="primary" round :loading="compSaving" @click="saveCompEdit">保存</el-button>
      </span>
    </el-dialog>

    <!-- 测试邮件 -->
    <el-dialog title="发送测试邮件" :visible.sync="testVisible" width="380px" custom-class="yw-dialog">
      <el-form label-position="top">
        <el-form-item label="接收邮箱"><el-input v-model="testEmail" placeholder="test@example.com" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button round @click="testVisible = false">取消</el-button>
        <el-button type="primary" round @click="sendTest">发送</el-button>
      </span>
    </el-dialog>
  </dashboard-layout>
</template>

<script>
import { adminApi } from "@/services/api";
import DashboardLayout from "@/components/DashboardLayout.vue";

export default {
  name: "AdminView",
  components: { DashboardLayout },
  data() {
    return {
      tab: "users",
      tabs: [
        {
          key: "users",
          label: "用户与额度",
          icon: "el-icon-user",
        },
        {
          key: "plans",
          label: "套餐管理",
          icon: "el-icon-present",
        },
        {
          key: "system-cards",
          label: "系统角色卡",
          icon: "el-icon-postcard",
        },
        {
          key: "companions",
          label: "全部伴侣",
          icon: "el-icon-s-custom",
        },
        {
          key: "audit",
          label: "审计日志",
          icon: "el-icon-document-checked",
        },
        {
          key: "prompt",
          label: "系统提示词",
          icon: "el-icon-edit-outline",
        },
        {
          key: "smtp",
          label: "SMTP 配置",
          icon: "el-icon-message",
        },
        {
          key: "gateway",
          label: "网关状态",
          icon: "el-icon-connection",
        },
      ],
      users: [],
      plans: [],
      systemRoleCards: [],
      companions: [],
      logs: [],
      smtp: { host: "", port: 587, username: "", sender: "", ssl: false, configured: false, password: "" },
      globalPrompt: { prompt: "", updatedAt: "" },
      gateway: { healthy: false, uptime: "-", accounts: [] },
      gatewayConfig: { message_buffer_window_ms: 5000, interrupt_generation: true, updatedAt: "" },
      quotaVisible: false,
      quotaRow: null,
      userSubVisible: false,
      userSubRow: null,
      userSubPlanCode: "",
      userSubSaving: false,
      planVisible: false,
      planForm: null,
      planSaving: false,
      testVisible: false,
      testEmail: "",
      cardsVisible: false,
      cardsLoading: false,
      cardsUser: null,
      userCards: [],
      accessUsersVisible: false,
      accessUsersLoading: false,
      accessSavingUserId: null,
      accessCard: null,
      accessGrantUserIds: [],
      accessUserQuery: "",
      compEditVisible: false,
      compSaving: false,
      compEditRow: null,
      compForm: null,
      promptSaving: false,
      gatewayConfigSaving: false,
      initialLoading: true,
    };
  },
  computed: {
    onlineCount() {
      return this.gateway.accounts.filter((a) => a.online).length;
    },
    filteredAccessUsers() {
      const keyword = this.accessUserQuery.trim().toLowerCase();
      if (!keyword) return this.users;
      return this.users.filter((user) =>
        [user.nickname, user.email, user.id].some((value) => String(value || "").toLowerCase().includes(keyword))
      );
    },
    grantedOrdinaryUserCount() {
      const ordinaryUserIds = new Set(this.users.filter((user) => user.role !== "admin").map((user) => user.id));
      return this.accessGrantUserIds.filter((id) => ordinaryUserIds.has(id)).length;
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      try {
        const [users, plans, systemCards, comps, logs, prompt, gwConfig, smtp, gw] = await Promise.all([
          adminApi.users(),
          adminApi.subscriptionPlans(),
          adminApi.systemRoleCards(),
          adminApi.companions(),
          adminApi.auditLogs(),
          adminApi.globalSystemPrompt(),
          adminApi.gatewayConfig(),
          adminApi.smtp(),
          adminApi.gatewayStatus(),
        ]);
        this.users = (users || []).map((u) => {
          const sub = u.subscription || null;
          const limit = (sub && sub.message_limit) || 0;
          const used = (sub && sub.message_used) || 0;
          const banned = u.is_active === false;
          return {
            id: u.id,
            nickname: u.display_name,
            email: u.email,
            role: u.is_admin ? "admin" : "user",
            isActive: !banned,
            quota: limit,
            used: used,
            planName: (sub && sub.plan_name) || "",
            subscription: sub,
            status: banned ? "已封禁" : limit > 0 && used >= limit * 0.95 ? "额度告警" : "正常",
          };
        });
        this.plans = plans || [];
        this.systemRoleCards = (systemCards || []).map((card) => ({ ...card, accessModeSaving: false }));
        this.companions = (comps || []).map((c) => ({
          id: c.id,
          name: c.name,
          owner: c.owner_display_name || c.owner_email,
          wechat: c.wechat_account_id || "—",
          status: c.binding_status === "bound" ? "已绑定" : "未绑定",
          createdAt: (c.created_at || "").slice(0, 10),
          raw: c,
        }));
        this.logs = (logs || []).map((l) => ({
          time: (l.created_at || "").replace("T", " ").slice(0, 19),
          actor: l.actor_user_id != null ? "用户#" + l.actor_user_id : "system",
          action: l.action,
          target: l.target_type + (l.target_id ? " #" + l.target_id : ""),
          ip: "—",
        }));
        this.smtp = {
          host: smtp.host,
          port: smtp.port,
          username: smtp.username,
          sender: smtp.from_email,
          ssl: smtp.use_ssl || smtp.use_tls,
          configured: smtp.password_configured,
          password: "",
        };
        this.globalPrompt = {
          prompt: prompt.prompt || "",
          updatedAt: prompt.updated_at || "",
        };
        this.gatewayConfig = {
          message_buffer_window_ms: Number(gwConfig.message_buffer_window_ms) || 5000,
          interrupt_generation: gwConfig.interrupt_generation !== false,
          updatedAt: gwConfig.updated_at || "",
        };
        this.gateway = {
          healthy: gw.gateway_running,
          uptime: gw.gateway_running ? "运行中" : "未运行",
          accounts: (gw.accounts || []).map((a) => ({
            id: a.account_id,
            nickname: a.account_id,
            online: a.running,
            lastSeen: (a.last_inbound_at || "").replace("T", " ").slice(0, 19) || "—",
          })),
        };
      } catch (e) {
        this.$message.error(e.message || "加载管理数据失败");
      } finally {
        this.initialLoading = false;
      }
    },
    openQuota(row) {
      this.quotaRow = { ...row };
      this.quotaVisible = true;
    },
    openUserSubscription(row) {
      this.userSubRow = { ...row };
      this.userSubPlanCode = (row.subscription && row.subscription.plan_code) || "";
      this.userSubVisible = true;
    },
    openPlanEdit(row) {
      this.planForm = { companion_limit: 0, role_card_limit: 0, wechat_takeover_days: 0, allow_role_sharing: true, ...row };
      this.planVisible = true;
    },
    async openUserCards(row) {
      this.cardsUser = row;
      this.userCards = [];
      this.cardsVisible = true;
      this.cardsLoading = true;
      try {
        const cards = await adminApi.userRoleCards(row.id);
        this.userCards = (cards || []).filter((c) => !c.is_system);
      } catch (e) {
        this.$message.error(e.message || "加载角色卡失败");
      } finally {
        this.cardsLoading = false;
      }
    },
    async saveSystemCardAccessMode(row) {
      row.accessModeSaving = true;
      try {
        const updated = await adminApi.updateSystemRoleCardAccessMode(row.id, row.access_mode);
        Object.assign(row, updated, { accessModeSaving: false });
        this.$message.success("投放范围已更新");
      } catch (e) {
        this.$message.error(e.message || "更新投放范围失败");
        await this.reloadSystemRoleCards();
      } finally {
        row.accessModeSaving = false;
      }
    },
    async reloadSystemRoleCards() {
      const cards = await adminApi.systemRoleCards();
      this.systemRoleCards = (cards || []).map((card) => ({ ...card, accessModeSaving: false }));
    },
    async openRoleCardAccess(row) {
      this.accessCard = row;
      this.accessGrantUserIds = [];
      this.accessUserQuery = "";
      this.accessUsersVisible = true;
      this.accessUsersLoading = true;
      try {
        const grants = await adminApi.roleCardAccessGrants(row.id);
        this.accessGrantUserIds = (grants || []).map((grant) => grant.user_id);
      } catch (e) {
        this.$message.error(e.message || "加载内测用户失败");
      } finally {
        this.accessUsersLoading = false;
      }
    },
    hasRoleCardAccess(userId) {
      return this.accessGrantUserIds.includes(userId);
    },
    accessAvatarLabel(row) {
      return (row.nickname || row.email || "用").trim().slice(0, 1).toUpperCase();
    },
    accessAvatarStyle(row) {
      const hue = 248 + (Number(row.id || 0) % 5) * 7;
      return {
        background: `linear-gradient(145deg, hsl(${hue} 58% 58%), hsl(${hue + 12} 62% 46%))`,
      };
    },
    async toggleRoleCardAccess(row) {
      if (!this.accessCard) return;
      const granted = this.hasRoleCardAccess(row.id);
      this.accessSavingUserId = row.id;
      try {
        if (granted) {
          await adminApi.revokeRoleCardAccess(this.accessCard.id, row.id);
          this.accessGrantUserIds = this.accessGrantUserIds.filter((id) => id !== row.id);
          this.$message.success(`已撤销「${row.nickname || row.email}」的内测资格`);
        } else {
          await adminApi.grantRoleCardAccess(this.accessCard.id, row.id);
          this.accessGrantUserIds = [...this.accessGrantUserIds, row.id];
          this.$message.success(`已给「${row.nickname || row.email}」开通内测资格`);
        }
      } catch (e) {
        this.$message.error(e.message || "更新内测资格失败");
      } finally {
        this.accessSavingUserId = null;
      }
    },
    openCompEdit(row) {
      const c = row.raw || {};
      this.compEditRow = row;
      this.compForm = {
        name: c.name || "",
        description: c.description || "",
        role_card_id: c.role_card_id || 1,
        custom_prompt: c.custom_prompt || "",
        welcome_message: c.welcome_message || "",
        message_history_limit: c.message_history_limit || 12,
        image_generation_mode: c.image_generation_mode || "edit_first",
        intimacy_base_level: c.intimacy_base_level !== null && c.intimacy_base_level !== undefined
          ? c.intimacy_base_level
          : 30,
      };
      this.compEditVisible = true;
    },
    async saveCompEdit() {
      if (!this.compForm.name || !this.compForm.name.trim()) {
        this.$message.warning("请填写名称");
        return;
      }
      this.compSaving = true;
      try {
        const updated = await adminApi.updateCompanion(this.compEditRow.id, this.compForm);
        this.compEditRow.name = updated.name;
        this.compEditRow.raw = updated;
        this.compEditVisible = false;
        this.$message.success("已保存");
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      } finally {
        this.compSaving = false;
      }
    },
    async saveQuota() {
      try {
        const updated = await adminApi.updateMessageQuota(this.quotaRow.id, this.quotaRow.quota);
        const u = this.users.find((x) => x.id === this.quotaRow.id);
        const sub = updated.subscription || {};
        if (u) {
          u.quota = sub.message_limit || 0;
          u.used = sub.message_used || 0;
          u.subscription = updated.subscription || u.subscription;
        }
        this.quotaVisible = false;
        this.$message.success("消息额度已更新");
      } catch (e) {
        this.$message.error(e.message || "更新失败");
      }
    },
    toggleUserActive(row) {
      const ban = row.isActive;
      this.$confirm(ban ? `确定封禁用户「${row.nickname}」吗？封禁后将无法登录。` : `确定解封用户「${row.nickname}」吗？`, ban ? "封禁用户" : "解封用户", {
        confirmButtonText: ban ? "确定封禁" : "确定解封",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const updated = await adminApi.setUserActive(row.id, !ban);
            row.isActive = updated.is_active !== false;
            row.status = row.isActive ? "正常" : "已封禁";
            this.$message.success(ban ? "已封禁" : "已解封");
          } catch (e) {
            this.$message.error(e.message || "操作失败");
          }
        })
        .catch(() => {});
    },
    async saveUserSubscription() {
      if (!this.userSubPlanCode) {
        this.$message.warning("请选择套餐");
        return;
      }
      this.userSubSaving = true;
      try {
        const updated = await adminApi.updateUserSubscription(this.userSubRow.id, this.userSubPlanCode);
        const u = this.users.find((x) => x.id === this.userSubRow.id);
        if (u) {
          u.planName = (updated.subscription && updated.subscription.plan_name) || "";
          u.subscription = updated.subscription || null;
        }
        this.userSubVisible = false;
        this.$message.success("用户套餐已更新");
      } catch (e) {
        this.$message.error(e.message || "更新失败");
      } finally {
        this.userSubSaving = false;
      }
    },
    async refreshUserSubscription(row) {
      try {
        const updated = await adminApi.refreshUserSubscription(row.id);
        const u = this.users.find((x) => x.id === row.id);
        if (u) {
          u.planName = (updated.subscription && updated.subscription.plan_name) || "";
          u.subscription = updated.subscription || null;
        }
        this.$message.success("用户套餐额度已刷新");
      } catch (e) {
        this.$message.error(e.message || "刷新失败");
      }
    },
    async savePlan() {
      if (!this.planForm || !this.planForm.name || !this.planForm.name.trim()) {
        this.$message.warning("请填写套餐名称");
        return;
      }
      this.planSaving = true;
      try {
        const saved = await adminApi.updateSubscriptionPlan(this.planForm.code, this.planForm);
        const idx = this.plans.findIndex((x) => x.code === saved.code);
        if (idx >= 0) this.$set(this.plans, idx, saved);
        else this.plans.push(saved);
        this.planVisible = false;
        this.$message.success("套餐已保存");
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      } finally {
        this.planSaving = false;
      }
    },
    adminUnbind(row) {
      this.$confirm(`确定解绑伴侣「${row.name}」的微信吗？`, "管理员解绑", { type: "warning" })
        .then(async () => {
          try {
            await adminApi.unbindCompanion(row.id);
            row.status = "未绑定";
            row.wechat = "—";
            this.$message.success("已解绑");
          } catch (e) {
            this.$message.error(e.message || "解绑失败");
          }
        })
        .catch(() => {});
    },
    adminDelete(row) {
      this.$confirm(`确定删除伴侣「${row.name}」吗？该操作不可恢复。`, "管理员删除", { type: "warning" })
        .then(async () => {
          try {
            await adminApi.deleteCompanion(row.id);
            this.companions = this.companions.filter((c) => c.id !== row.id);
            this.$message.success("已删除");
          } catch (e) {
            this.$message.error(e.message || "删除失败");
          }
        })
        .catch(() => {});
    },
    async saveSmtp() {
      try {
        await adminApi.updateSmtp({
          host: this.smtp.host,
          port: Number(this.smtp.port) || 587,
          username: this.smtp.username,
          password: this.smtp.password,
          from_email: this.smtp.sender,
          use_ssl: this.smtp.ssl,
          use_tls: !this.smtp.ssl,
        });
        this.smtp.configured = true;
        this.$message.success("SMTP 配置已保存");
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      }
    },
    async sendTest() {
      try {
        await adminApi.testSmtp(this.testEmail);
        this.testVisible = false;
        this.$message.success("测试邮件已发送");
      } catch (e) {
        this.$message.error(e.message || "发送失败");
      }
    },
    async saveGlobalPrompt() {
      this.promptSaving = true;
      try {
        const saved = await adminApi.updateGlobalSystemPrompt(this.globalPrompt.prompt || "");
        this.globalPrompt.updatedAt = saved.updated_at || "";
        this.$message.success("系统提示词已保存");
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      } finally {
        this.promptSaving = false;
      }
    },
    async saveGatewayConfig() {
      this.gatewayConfigSaving = true;
      try {
        const saved = await adminApi.updateGatewayConfig({
          message_buffer_window_ms: Number(this.gatewayConfig.message_buffer_window_ms) || 0,
          interrupt_generation: this.gatewayConfig.interrupt_generation !== false,
        });
        this.gatewayConfig.message_buffer_window_ms = Number(saved.message_buffer_window_ms) || 0;
        this.gatewayConfig.interrupt_generation = saved.interrupt_generation !== false;
        this.gatewayConfig.updatedAt = saved.updated_at || "";
        this.$message.success("网关窗口已保存，重启网关后生效");
      } catch (e) {
        this.$message.error(e.message || "保存失败");
      } finally {
        this.gatewayConfigSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.admin-page {
  min-width: 0;

  .admin-toolbar {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: stretch;
    gap: 12px;
    margin-bottom: 14px;
  }

  .gateway-state {
    display: grid;
    min-width: 222px;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #efcbd1;
    border-radius: var(--xyo-radius-md);
    color: var(--xyo-danger);
    background: var(--xyo-danger-soft);

    &.ok {
      border-color: #bfe2d2;
      color: var(--xyo-success);
      background: var(--xyo-success-soft);
    }

    .gateway-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: currentColor;
      box-shadow: 0 0 0 4px rgba(195, 56, 79, 0.1);
    }

    &.ok .gateway-dot {
      box-shadow: 0 0 0 4px rgba(23, 128, 90, 0.11);
    }

    .gateway-copy {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 2px;

      small {
        color: var(--xyo-muted);
        font-size: 10px;
        line-height: 1.2;
      }

      strong {
        font-size: 13px;
        line-height: 1.25;
      }
    }

    .gateway-uptime {
      padding-left: 10px;
      border-left: 1px solid currentColor;
      font-size: 11px;
      opacity: 0.78;
      white-space: nowrap;
    }
  }

  .module-tabs {
    display: grid;
    width: 100%;
    min-width: 0;
    grid-template-columns: repeat(7, minmax(112px, 1fr));
    gap: 4px;
    margin-bottom: 0;
    padding: 5px;
    overflow-x: auto;
    border: 1px solid var(--xyo-line);
    border-radius: var(--xyo-radius-md);
    background: var(--xyo-surface-raised);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .module-tab {
    position: relative;
    display: inline-flex;
    min-height: 42px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 10px;
    overflow: hidden;
    border: 0;
    border-radius: 8px;
    color: var(--xyo-muted);
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 650;
    line-height: 1.2;
    cursor: pointer;
    transition: color 180ms ease, background-color 180ms ease, transform 180ms ease;

    &::after {
      position: absolute;
      right: 12px;
      bottom: 0;
      left: 12px;
      height: 2px;
      border-radius: 999px 999px 0 0;
      background: var(--xyo-accent);
      content: "";
      opacity: 0;
      transform: scaleX(0.35);
      transition: opacity 180ms ease, transform 180ms ease;
    }

    i {
      font-size: 15px;
    }

    &:hover {
      color: var(--xyo-ink-strong);
      background: var(--xyo-surface-muted);
    }

    &.on {
      color: var(--xyo-accent);
      background: var(--xyo-accent-pale);

      &::after {
        opacity: 1;
        transform: scaleX(1);
      }
    }
  }

  .admin-workspace {
    min-width: 0;
    overflow: hidden;
    border: 1px solid var(--xyo-line);
    border-radius: var(--xyo-radius-lg);
    background: var(--xyo-surface-raised);
  }

  .workspace-content,
  .module-panel {
    min-width: 0;
  }

  .module-panel {
    padding: 0 24px 24px;
    animation: admin-panel-enter 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .section-note {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 52px;
    color: var(--xyo-ink-soft);
    font-size: 12px;

    span {
      font-weight: 650;
    }

    small {
      color: var(--xyo-muted);
      font-size: 11px;
    }
  }

  .user-cell,
  .entity-cell,
  .plan-name-cell {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 10px;
  }

  .user-avatar,
  .entity-icon {
    display: inline-flex;
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    border: 1px solid #ddd5f8;
    border-radius: 10px;
    color: var(--xyo-accent);
    background: var(--xyo-accent-pale);
    font-size: 13px;
    font-style: normal;
    font-weight: 750;
  }

  .user-copy,
  .entity-cell > span:last-child,
  .plan-name-cell > span:last-child {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
  }

  .user-name-line,
  .sub-name-line {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
  }

  .user-name-line b,
  .entity-cell b,
  .plan-name-cell b {
    overflow: hidden;
    color: var(--xyo-ink-strong);
    font-size: 13px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-cell small,
  .entity-cell small,
  .plan-name-cell small {
    overflow: hidden;
    color: var(--xyo-muted);
    font-size: 10px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .role-tag {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    padding: 2px 6px;
    border-radius: 999px;
    color: var(--xyo-muted);
    background: var(--xyo-surface-muted);
    font-size: 9px;
    font-style: normal;
    font-weight: 700;
    line-height: 1.4;

    &.admin {
      color: var(--xyo-accent);
      background: var(--xyo-accent-soft);
    }
  }

  .sub-cell {
    position: relative;
    display: flex;
    min-width: 0;
    align-items: stretch;
    gap: 9px;
    padding: 8px 10px 8px 0;
    border: 1px solid #e6dff8;
    border-radius: 9px;
    background: var(--xyo-accent-pale);

    .sub-accent {
      width: 3px;
      flex: 0 0 auto;
      border-radius: 0 999px 999px 0;
      background: var(--xyo-accent);
    }

    .sub-copy {
      display: flex;
      min-width: 0;
      flex: 1;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
    }

    .sub-name-line {
      justify-content: space-between;

      b {
        overflow: hidden;
        color: var(--xyo-ink-strong);
        font-size: 12px;
        line-height: 1.25;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      em {
        flex: 0 0 auto;
        padding: 2px 6px;
        border-radius: 999px;
        color: var(--xyo-success);
        background: var(--xyo-success-soft);
        font-size: 9px;
        font-style: normal;
        font-weight: 700;
      }
    }

    small {
      overflow: hidden;
      color: var(--xyo-muted);
      font-size: 10px;
      line-height: 1.3;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.empty {
      border-color: var(--xyo-line);
      background: var(--xyo-surface-muted);

      .sub-accent {
        background: var(--xyo-control-border);
      }
    }

    &.expired {
      border-color: #efcbd1;
      background: var(--xyo-danger-soft);

      .sub-accent {
        background: var(--xyo-danger);
      }

      .sub-name-line em {
        color: var(--xyo-danger);
        background: #fff;
      }
    }
  }

  .quota-cell {
    min-width: 0;

    .quota-copy {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 7px;
      color: var(--xyo-muted);
      font-size: 10px;
      font-variant-numeric: tabular-nums;

      b {
        color: var(--xyo-ink-strong);
        font-size: 12px;
      }

      em {
        color: var(--xyo-primary);
        font-size: 10px;
        font-style: normal;
        font-weight: 700;
      }
    }
  }

  .status-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    color: var(--xyo-success);
    background: var(--xyo-success-soft);
    font-size: 10px;
    font-weight: 700;
    white-space: nowrap;

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.warn {
      color: var(--xyo-warning);
      background: var(--xyo-warning-soft);
    }

    &.banned {
      color: var(--xyo-danger);
      background: var(--xyo-danger-soft);
    }

    &.bound {
      color: var(--xyo-success);
      background: var(--xyo-success-soft);
    }

    &.off {
      color: var(--xyo-muted);
      background: var(--xyo-surface-muted);
    }
  }

  .row-actions {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    button {
      min-height: 28px;
      padding: 5px 7px;
      border: 1px solid transparent;
      border-radius: 7px;
      color: var(--xyo-ink-soft);
      background: transparent;
      font-family: inherit;
      font-size: 10px;
      font-weight: 650;
      line-height: 1.2;
      cursor: pointer;
      white-space: nowrap;
      transition: color 160ms ease, border-color 160ms ease, background-color 160ms ease;

      &:hover,
      &:focus-visible {
        border-color: #d8cdf9;
        color: var(--xyo-accent);
        background: var(--xyo-accent-pale);
      }

      &.danger {
        color: var(--xyo-danger);

        &:hover,
        &:focus-visible {
          border-color: #efcbd1;
          color: var(--xyo-danger);
          background: var(--xyo-danger-soft);
        }
      }

      &.success {
        color: var(--xyo-success);

        &:hover,
        &:focus-visible {
          border-color: #bfe2d2;
          color: var(--xyo-success);
          background: var(--xyo-success-soft);
        }
      }
    }

    &.single button {
      padding-right: 11px;
      padding-left: 11px;
      border-color: var(--xyo-line-strong);
      background: var(--xyo-surface-raised);
    }
  }

  .plan-swatch {
    width: 4px;
    height: 32px;
    flex: 0 0 auto;
    border-radius: 999px;
  }

  .price-value {
    color: var(--xyo-ink-strong);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  .recommend-tag {
    display: inline-flex;
    padding: 3px 7px;
    border-radius: 999px;
    color: var(--xyo-muted);
    background: var(--xyo-surface-muted);
    font-size: 10px;
    font-weight: 700;

    &.on {
      color: var(--xyo-primary);
      background: var(--xyo-warm-soft);
    }
  }

  .action-code {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    padding: 3px 6px;
    border-radius: 6px;
    color: var(--xyo-ink-soft);
    background: var(--xyo-surface-muted);
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .admin-callout {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    margin: 20px 0 22px;
    padding: 14px 16px;
    border: 1px solid #ddd5f8;
    border-radius: var(--xyo-radius-md);
    background: var(--xyo-accent-pale);

    .callout-icon {
      display: inline-flex;
      width: 34px;
      height: 34px;
      align-items: center;
      justify-content: center;
      border-radius: 9px;
      color: var(--xyo-accent);
      background: var(--xyo-accent-soft);
      font-size: 15px;
    }

    .callout-copy {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 3px;

      b {
        color: var(--xyo-ink-strong);
        font-size: 12px;
      }

      small {
        color: var(--xyo-muted);
        font-size: 10px;
        line-height: 1.55;
      }
    }

    > button {
      display: inline-flex;
      min-height: 34px;
      align-items: center;
      gap: 6px;
      padding: 7px 10px;
      border: 1px solid #d2c6f7;
      border-radius: 8px;
      color: var(--xyo-accent);
      background: #fff;
      font-family: inherit;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;

      &:hover {
        border-color: var(--xyo-accent);
      }
    }
  }

  .prompt-form {
    max-width: 980px;
  }

  .prompt-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin: -8px 0 18px;
    color: var(--xyo-muted);
    font-size: 10px;
    line-height: 1.5;
  }

  .config-status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin: 18px 0 4px;
    padding: 5px 9px;
    border-radius: 999px;
    color: var(--xyo-warning);
    background: var(--xyo-warning-soft);
    font-size: 10px;
    font-weight: 700;

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.ok {
      color: var(--xyo-success);
      background: var(--xyo-success-soft);
    }
  }

  .smtp-form {
    max-width: 1080px;
    margin-top: 18px;
  }

  .smtp-security-row {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(220px, 1fr);
    gap: 0 16px;

    .ssl-field {
      padding: 0 14px;
      border: 1px solid var(--xyo-line);
      border-radius: 10px;
      background: var(--xyo-surface-muted);

      ::v-deep .el-form-item__label {
        padding-top: 10px;
      }

      ::v-deep .el-form-item__content {
        padding-bottom: 9px;
      }
    }
  }

  .form-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-top: 2px;
  }

  .gateway-settings {
    margin-top: 8px;
  }

  .setting-row {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 18px 0;
    border-bottom: 1px solid var(--xyo-line);

    > div:first-child {
      min-width: 0;
    }

    b {
      display: block;
      margin-bottom: 5px;
      color: var(--xyo-ink-strong);
      font-size: 13px;
    }

    p {
      max-width: 760px;
      margin: 0;
      color: var(--xyo-muted);
      font-size: 11px;
      line-height: 1.55;
    }

    .setting-actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 9px;
      white-space: nowrap;
    }

    .unit {
      color: var(--xyo-muted);
      font-size: 10px;
    }
  }

  .gw-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 18px 0 22px;
    border-top: 1px solid var(--xyo-line);
    border-bottom: 1px solid var(--xyo-line);

    > div {
      display: flex;
      min-height: 86px;
      flex-direction: column;
      justify-content: center;
      gap: 7px;
      padding: 14px 18px;
      border-right: 1px solid var(--xyo-line);

      &:last-child {
        border-right: 0;
      }
    }

    span {
      color: var(--xyo-muted);
      font-size: 10px;
      font-weight: 650;
    }

    b {
      color: var(--xyo-ink-strong);
      font-size: 20px;
      font-variant-numeric: tabular-nums;

      &.ok {
        color: var(--xyo-success);
      }

      small {
        color: var(--xyo-muted);
        font-size: 11px;
        font-weight: 600;
      }
    }
  }

  .gateway-accounts-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 12px;

    b {
      color: var(--xyo-ink-strong);
      font-size: 13px;
    }

    small {
      color: var(--xyo-muted);
      font-size: 10px;
    }
  }

  ::v-deep .admin-table {
    width: 100%;
    border-color: var(--xyo-line);
    border-radius: var(--xyo-radius-md);

    th.el-table__cell {
      padding-top: 10px;
      padding-bottom: 10px;
      color: var(--xyo-muted);
      background: var(--xyo-surface-muted) !important;
      font-size: 10px;
      letter-spacing: 0.04em;
    }

    td.el-table__cell {
      padding-top: 10px;
      padding-bottom: 10px;
    }

    .cell {
      padding-right: 10px;
      padding-left: 10px;
    }

    .el-progress-bar__outer {
      background: #ebe7ef;
    }
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
}

.empty-tip {
  margin: 12px 0 0;
  color: var(--xyo-muted);
  font-size: 12px;
  text-align: center;
}

@keyframes admin-panel-enter {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .admin-page {
    .admin-toolbar {
      grid-template-columns: 1fr;
    }

    .gateway-state {
      width: 100%;
      min-width: 0;
    }

    .smtp-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .module-panel {
      padding-right: 18px;
      padding-left: 18px;
    }
  }
}

@media (max-width: 768px) {
  .admin-page {
    max-width: 100%;

    .module-tabs {
      grid-template-columns: repeat(7, minmax(124px, 1fr));
    }

    .module-panel {
      padding: 0 12px 16px;
      overflow-x: hidden;
    }

    .section-note {
      align-items: flex-start;
      flex-direction: column;
      justify-content: center;
      gap: 3px;
      padding: 10px 0;
    }

    .admin-callout {
      grid-template-columns: auto minmax(0, 1fr);

      > button {
        grid-column: 1 / -1;
        justify-content: center;
      }
    }

    .prompt-meta,
    .setting-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .setting-row .setting-actions {
      width: 100%;
      flex-wrap: wrap;
      white-space: normal;
    }

    .smtp-grid,
    .smtp-security-row {
      grid-template-columns: 1fr;
    }

    .smtp-security-row .ssl-field {
      margin-bottom: 20px;
    }
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .admin-page {
    .gateway-state {
      grid-template-columns: auto minmax(0, 1fr);

      .gateway-uptime {
        display: none;
      }
    }

    .gw-summary {
      grid-template-columns: 1fr;

      > div {
        min-height: 66px;
        border-right: 0;
        border-bottom: 1px solid var(--xyo-line);

        &:last-child {
          border-bottom: 0;
        }
      }
    }

    .form-actions {
      align-items: stretch;
      flex-direction: column;

      ::v-deep .el-button {
        width: 100%;
        margin-left: 0;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .admin-page .module-panel {
    animation: none;
  }

  .admin-page .module-tab,
  .admin-page .module-tab::after,
  .admin-page .row-actions button {
    transition: none;
  }
}
</style>

<style scoped lang="scss">
::v-deep .access-users-dialog {
  width: min(720px, calc(100vw - 28px)) !important;
  margin-top: min(10vh, 84px) !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 20px 24px 18px;
    border-bottom: 1px solid var(--xyo-line);
  }

  .el-dialog__headerbtn {
    top: 22px;
    right: 22px;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    transition: background-color 180ms ease, color 180ms ease;

    &:hover,
    &:focus-visible {
      background: var(--xyo-surface-muted);
    }
  }

  .el-dialog__body {
    padding: 0 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px 20px;
    border-top: 1px solid var(--xyo-line);
  }
}

.access-dialog-title {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 44px;
}

.access-title-icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  border: 1px solid #dfd8f8;
  border-radius: 11px;
  color: var(--xyo-primary);
  background: var(--xyo-accent-pale);
  font-size: 16px;
}

.access-title-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;

  b {
    overflow: hidden;
    color: var(--xyo-ink-strong);
    font-size: 16px;
    font-weight: 750;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: var(--xyo-muted);
    font-size: 11px;
    font-weight: 600;
  }
}

.access-dialog-body {
  min-width: 0;
}

.access-intro {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 16px 0;
  padding: 11px 13px;
  border: 1px solid #e5e0f6;
  border-radius: 11px;
  color: var(--xyo-ink-soft);
  background: #faf9fe;

  i {
    margin-top: 2px;
    color: var(--xyo-primary);
    font-size: 14px;
  }

  p {
    margin: 0;
    font-size: 12px;
    line-height: 1.65;
  }
}

.access-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;

  ::v-deep .el-input {
    max-width: 340px;

    .el-input__inner {
      height: 40px;
      border-color: var(--xyo-line);
      border-radius: 10px;
      color: var(--xyo-ink-strong);
      background: var(--xyo-surface-raised);

      &:focus {
        border-color: var(--xyo-primary);
        box-shadow: 0 0 0 3px rgba(111, 86, 217, 0.1);
      }
    }
  }
}

.access-summary {
  display: flex;
  align-items: baseline;
  gap: 5px;
  white-space: nowrap;

  b {
    color: var(--xyo-primary);
    font-size: 18px;
    font-variant-numeric: tabular-nums;
  }

  small {
    color: var(--xyo-muted);
    font-size: 11px;
  }
}

.access-list {
  position: relative;
  max-height: min(430px, 50vh);
  min-height: 120px;
  margin-bottom: 16px;
  overflow-y: auto;
  border: 1px solid var(--xyo-line);
  border-radius: 12px;
  background: var(--xyo-surface-raised);
  scrollbar-width: thin;
}

.access-list-head,
.access-user-row {
  display: grid;
  grid-template-columns: minmax(250px, 1fr) 104px 136px;
  align-items: center;
  column-gap: 12px;
}

.access-list-head {
  position: sticky;
  z-index: 2;
  top: 0;
  min-height: 38px;
  padding: 0 14px;
  border-bottom: 1px solid var(--xyo-line);
  color: var(--xyo-muted);
  background: #f8f7fb;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;

  span:last-child {
    text-align: right;
  }
}

.access-user-row {
  min-height: 68px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--xyo-line);
  transition: background-color 180ms ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: #fbfaff;
  }

  &.admin {
    background: #fcfbff;
  }

  &.banned .access-user-avatar,
  &.banned .access-user-copy {
    opacity: 0.58;
  }
}

.access-user-identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
}

.access-user-avatar {
  display: inline-flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.88);
  border-radius: 12px;
  color: #fff;
  box-shadow: 0 3px 10px rgba(86, 68, 156, 0.18);
  font-size: 14px;
  font-weight: 750;
  text-transform: uppercase;
}

.access-user-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;

  small {
    overflow: hidden;
    color: var(--xyo-muted);
    font-size: 11px;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.access-user-name-line {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;

  b {
    overflow: hidden;
    color: var(--xyo-ink-strong);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.access-role-badge {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 999px;
  color: var(--xyo-primary);
  background: var(--xyo-accent-pale);
  font-size: 9px;
  font-style: normal;
  font-weight: 700;
}

.access-account-state {
  min-width: 0;
}

.access-state-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--xyo-success);
  font-size: 11px;
  font-weight: 650;

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &.off {
    color: var(--xyo-danger);
  }
}

.access-control {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;

  ::v-deep .el-switch {
    flex: 0 0 auto;
  }
}

.access-control-label {
  min-width: 48px;
  color: var(--xyo-muted);
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;

  &.on {
    color: var(--xyo-primary);
  }
}

.access-empty {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: var(--xyo-muted);

  i {
    margin-bottom: 3px;
    font-size: 22px;
  }

  b {
    color: var(--xyo-ink-soft);
    font-size: 13px;
  }

  small {
    font-size: 11px;
  }
}

.access-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  > small {
    color: var(--xyo-muted);
    font-size: 11px;
  }

  ::v-deep .el-button {
    min-width: 94px;
    min-height: 40px;
  }
}

@media (max-width: 640px) {
  ::v-deep .access-users-dialog {
    margin-top: 16px !important;

    .el-dialog__header {
      padding: 16px 18px;
    }

    .el-dialog__body {
      padding: 0 16px;
    }

    .el-dialog__footer {
      padding: 14px 16px 16px;
    }
  }

  .access-toolbar {
    grid-template-columns: 1fr;
    gap: 8px;

    ::v-deep .el-input {
      max-width: none;
    }
  }

  .access-summary {
    justify-self: end;
  }

  .access-list-head {
    display: none;
  }

  .access-user-row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 12px;
    min-height: 82px;
    padding: 12px;
  }

  .access-user-identity {
    grid-row: 1 / 3;
  }

  .access-account-state {
    justify-self: end;
  }

  .access-control {
    min-height: 32px;
    justify-self: end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .access-user-row,
  ::v-deep .access-users-dialog .el-dialog__headerbtn {
    transition: none;
  }
}
</style>
